import { quoteLines, allowsPointDiscounts, drawButtonState, shouldShowPayableAmount, payableAmount, formatPoints } from './draw-quote.mjs';
import { statusMessage, isTimedOut, shouldStopPolling, nextIntervalMs, shouldUseStoredDrawOrder, shouldStopOnTransportFailure, DEFAULT_INTERVAL_MS } from './draw-polling.mjs';
import { createDrawPollScheduler } from './draw-poll-scheduler.mjs';
import { canSubmitCard, cardStatusMessage, cardOutcomeAction } from './draw-card.mjs';
import { offlineNoticeText } from './draw-gateway.mjs';
import { notifyEmailError } from './notify-email.mjs';
import { createPaySection } from './pay-section.js';
import { createTapPayCardFields } from './tappay-card.mjs';
import { DRAW_STATUS_EVENT, drawStatusOrderId } from './draw-status-event.mjs';
import { treasureStopOptionVisible } from './draw-treasure-stop.mjs';

/**
 * 抽獎付款彈窗的共用 DOM 層：gateway 清單、TapPay iframe 掛載、發票欄位切換、
 * 付款方式選擇的保存與還原、確認鈕狀態、回程輪詢的 DOM。一番賞與轉蛋共用同一份
 * markup（見 templates/partials/draw-pay-popup.php），差異點（selection、drawApi、
 * dispatch 進哪個 reducer）全部靠 `deps` 注入，本模組不認 kind。
 *
 * `.js` 不是 `.mjs`：含 DOM，不納入 `node --test`（決議 18）。
 *
 * @param {object} deps
 * @param {object} deps.$ jQuery
 * @param {object} deps.drawApi 需有 reserve(payload) / card(payload) / status(orderId) / discountFields(flags)
 * @param {() => number} deps.getProductId
 * @param {() => number[]} deps.getSelection 轉蛋恆回 []
 * @param {() => object} [deps.getExtraPayload] reserve/card 請求要額外帶的欄位（轉蛋的 draws、一番賞的 treasure_open_all），沒有就不加
 * @param {() => object} deps.getDiscountFlags
 * @param {() => {quote: ?object, quoting: boolean}} deps.getFlow 目前的報價狀態（同意條款切換時用）
 * @param {(event: object) => object} deps.dispatch 送進呼叫端自己的 draw-flow reducer，
 *   必須回傳 reduce 後的新 state（呼叫端在送出 API 前要靠回傳值的 `phase` 判斷是否真的進了 submitting）
 * @param {() => boolean} deps.hasPeel
 * @param {(res: object) => boolean} deps.handleBlockedResponse 待付單擋下時的處理，true = 已接手
 * @param {() => void} [deps.onDrawSent] reserve / card 請求即將送出時呼叫（前端檢查與取 prime 都已通過）；
 *   一番賞頁用來累加商品熱度，轉蛋不注入
 */
export function createDrawPayPopup(deps) {
    var $ = deps.$;
    var drawApi = deps.drawApi;

    function escHtml(str) {
        return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }

    var DRAW_CHECK_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"' +
        ' stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="m5 13 4 4L19 7"/></svg>';

    /**
     * 明細列有兩種列型：沒有 toggle 的是單行資訊列（左標題右金額），有 toggle 的是
     * 折抵列——視覺隱藏的真 checkbox 留在 DOM 吃鍵盤與輔助技術，圓框勾號只是它的
     * 視覺外殼，名稱與餘額排成兩行。
     * class 前綴維持 `wtoy-draw-pay-`：`.wtoy-draw-pay-toggle` 的 change 委派掛在
     * document.body，與出貨窗共用 class 會讓兩邊的勾選互相觸發。
     */
    function drawPayLineHtml(line) {
        if (!line.toggle) {
            return '<div class="wtoy-draw-pay-info-row">' +
                '<span class="wtoy-draw-pay-info-label">' + escHtml(line.label) + '</span>' +
                '<span class="wtoy-draw-pay-info-value">' + escHtml(line.value) + '</span>' +
            '</div>';
        }

        var id = 'wtoy-draw-pay-discount-' + line.toggle;

        return '<div class="wtoy-draw-pay-discount-row">' +
            '<input type="checkbox" class="wtoy-draw-pay-toggle" id="' + escHtml(id) + '"' +
                ' data-flag="' + escHtml(line.toggle) + '"' + (line.checked ? ' checked' : '') + '>' +
            '<label class="wtoy-draw-pay-discount-label" for="' + escHtml(id) + '">' +
                '<span class="wtoy-draw-pay-discount-box" aria-hidden="true">' + DRAW_CHECK_SVG + '</span>' +
                '<span class="wtoy-draw-pay-discount-text">' +
                    '<span class="wtoy-draw-pay-discount-name">' + escHtml(line.label) + '</span>' +
                    '<span class="wtoy-draw-pay-discount-balance">' + escHtml(line.balanceText) + '</span>' +
                '</span>' +
            '</label>' +
            '<span class="wtoy-draw-pay-discount-amount">' + escHtml(line.value) + '</span>' +
        '</div>';
    }

    var tapPayCardUpdate = null;

    // 本次彈窗開啟後是否已套用過跨次記憶的付款偏好：彈窗開啟時重設，套用後置起，
    // 之後同一次開啟期間的重繪（折抵切換等）一律走既有的 capture/restore，
    // 不讓偏好蓋掉使用者當下已經改過的選擇（陷阱 3.3）。
    var preferenceApplied = false;

    var paySection = createPaySection({ $: $, offlineNotice: offlineNoticeText });
    var tapPayCard = createTapPayCardFields({
        onStatusChange: function(update) {
            tapPayCardUpdate = update;
            $('#wtoy_draw_pay_card_message').text(cardStatusMessage(update.status));
        },
    });

    // gateway 的 change 事件命名空間（change.wtoyDrawGateway）綁在 paySection.renderGateways()
    // 內；卡號欄的顯隱同樣交給它處理，這裡只在切到當頁刷卡時掛載 TapPay SDK。
    // 綁在 document（非 document.body）：本行在工廠本體頂層執行，一番賞頁主腳本印在
    // <head>，此刻 document.body 還是 null，同綁 body 會靜默失效（見下方回流關閉監聽的同款註解）。
    $(document).off('change.wtoyDrawPayCardSetup').on('change.wtoyDrawPayCardSetup', 'input[name=wtoy_draw_pay_gateway]', function() {
        if ($(this).data('redirect-mode') === 'in_popup') {
            tapPayCard.setupTapPayCardFields();
        }
    });

    /** 收起「免單停止」問號氣泡。展開狀態只在 DOM 上（.is-open 與 aria-expanded），沒有別處的狀態要同步。 */
    function collapseTreasureStopHelp() {
        $('.wtoy-draw-pay-treasure-stop-help-wrap').removeClass('is-open')
            .find('.wtoy-draw-pay-treasure-stop-help').attr('aria-expanded', 'false');
    }

    function renderDrawPayPopup(quote) {
        $('#wtoy_draw_pay_product_image').attr('src', quote.product_image || '');
        $('#wtoy_draw_pay_product_name').text(quote.product_name);
        $('#wtoy_draw_pay_unit_price').text('NT$' + formatPoints(quote.unit_price));
        $('#wtoy_draw_pay_quantity').text(quote.qty);
        $('#wtoy_draw_pay_total_quantity').text(quote.qty);
        $('#wtoy_draw_pay_total_amount').text(payableAmount(quote));
        $('#wtoy_draw_pay_total_amount_row').prop('hidden', !shouldShowPayableAmount(quote));

        $('#wtoy_draw_pay_lines').html(quoteLines(quote).map(drawPayLineHtml).join(''));
        // 點數、玩瘋券都被商品禁用時，換算說明沒有對象，跟著「折抵合計」一起藏
        $('#wtoy_draw_pay_discount_note').prop('hidden', !allowsPointDiscounts(quote));
        var agreed = $('#wtoy_draw_pay_agree').is(':checked');
        var state = drawButtonState(quote, agreed);
        $('#wtoy_draw_pay_confirm').text(state.label).prop('disabled', state.disabled);
        $('#wtoy_draw_pay_block_reason').text(quote.can_draw ? '' : (quote.block_reason || ''));

        // 只切換顯示、不碰 checkbox 的勾選值：折抵勾選觸發重新報價整份重繪時，客人的選擇不能被洗掉
        var showStopOption = treasureStopOptionVisible(quote);
        $('#wtoy_draw_pay_treasure_stop').prop('hidden', !showStopOption);
        if (!showStopOption) {
            collapseTreasureStopHelp();
        }

        var showPayment = paySection.applyVisibility(quote);
        if (showPayment) {
            // 勾選變動會整份重繪，已選的付款方式與已填的發票欄位不能被洗掉
            var picked = paySection.capturePaymentChoice();
            paySection.renderGateways(quote.gateways || []);
            paySection.setBuyerName(quote.buyer_name);
            paySection.renderInvoiceTypes(quote.invoice_types || [], quote.donation_groups);
            if (!preferenceApplied) {
                preferenceApplied = true;
                paySection.applyPreference(quote.pay_preference);
            } else {
                paySection.restorePaymentChoice(picked);
            }
        }

        $(document.body).off('change.wtoyDrawPayToggle').on('change.wtoyDrawPayToggle', '.wtoy-draw-pay-toggle', function() {
            // 勾選即刻進 state，帶 checkbox 的新值而非翻轉；壓縮的是網路請求，不是使用者的意思
            deps.dispatch({ type: 'discountToggled', flag: $(this).data('flag'), enabled: this.checked });
        });

        $(document.body).off('change.wtoyDrawPayAgree').on('change.wtoyDrawPayAgree', '#wtoy_draw_pay_agree', function() {
            // 報價還在路上時畫面金額已作廢，解鎖會讓使用者按著舊金額送出新旗標
            var flow = deps.getFlow();
            if (!flow.quote || flow.quoting) {
                return;
            }
            var s = drawButtonState(flow.quote, $(this).is(':checked'));
            $('#wtoy_draw_pay_confirm').text(s.label).prop('disabled', s.disabled);
        });
    }

    /** email 欄在付款方式上方，先檢查它。合法回填寫值；不合法 alert 並聚焦，回 null。 */
    function requireNotifyEmail() {
        var email = paySection.notifyEmail();
        var message = notifyEmailError(email);
        if (message) {
            alert(message);
            $('#wtoy_draw_pay_email_input').trigger('focus');
            return null;
        }
        return email;
    }

    function confirmReserve() {
        if (requireNotifyEmail() === null) {
            return;
        }
        var gatewayId = $('input[name=wtoy_draw_pay_gateway]:checked').val();
        if (!gatewayId) {
            alert('請選擇付款方式');
            return;
        }
        var invoiceType = $('input[name=wtoy_draw_pay_invoice_type]:checked').val();
        if (!invoiceType) {
            alert('請選擇發票類型');
            return;
        }
        var invoiceFields = paySection.collectInvoiceFields();
        if (invoiceType !== 'company' && !String(invoiceFields.billing_owp_wcaei_buyer_name || '').trim()) {
            alert('請填寫買受人');
            return;
        }

        var payload = $.extend({
            product_id: deps.getProductId(),
            selection: deps.getSelection(),
            gateway_id: gatewayId,
            billing_email: paySection.notifyEmail(),
            billing_owp_wcaei_invoice_type: invoiceType,
        }, drawApi.discountFields(deps.getDiscountFlags()), invoiceFields, deps.getExtraPayload ? deps.getExtraPayload() : {});

        var afterSubmit = deps.dispatch({ type: 'submitReserve' });
        if (afterSubmit.phase !== 'submitting') {
            return;
        }

        if (deps.onDrawSent) {
            deps.onDrawSent();
        }

        drawApi.reserve(payload).then(function(res) {
            if (!res.success) {
                if (!deps.handleBlockedResponse(res)) {
                    alert(res.data && res.data.message);
                    // 被擋下多半是號碼已被別人拿走，盤面已經過期；轉蛋沒有盤面，不注入這個 dep
                    if (deps.onBoardStale) {
                        deps.onBoardStale();
                    }
                }
                deps.dispatch({ type: 'submitBlocked' });
                return;
            }
            // 付款進行中旗標：離開頁面去金流不觸發「確定要離開嗎」的提示，也供回流頁輪詢
            // 讀取判斷。帶 productId 一併存，回流頁比對用（見 getDrawReturnOrderId()）
            sessionStorage.setItem('wtoy_draw_paying', JSON.stringify({
                orderId: res.data.order_id,
                productId: deps.getProductId(),
            }));
            deps.dispatch({ type: 'redirecting', url: res.data.redirect_url });
        }, function() {
            alert('系統異常，請稍後再試。');
            deps.dispatch({ type: 'submitBlocked' });
        });
    }

    function submitCardDraw(gatewayId, invoiceType, prime) {
        var payload = $.extend({
            product_id: deps.getProductId(),
            selection: deps.getSelection(),
            gateway_id: gatewayId,
            prime: prime,
            billing_email: paySection.notifyEmail(),
            billing_owp_wcaei_invoice_type: invoiceType,
        }, drawApi.discountFields(deps.getDiscountFlags()), paySection.collectInvoiceFields(), deps.getExtraPayload ? deps.getExtraPayload() : {});

        if (deps.onDrawSent) {
            deps.onDrawSent();
        }

        drawApi.card(payload).then(function(res) {
            if (!res.success) {
                if (!deps.handleBlockedResponse(res)) {
                    alert(res.data && res.data.message);
                    if (deps.onBoardStale) {
                        deps.onBoardStale();
                    }
                }
                deps.dispatch({ type: 'submitBlocked' });
                return;
            }
            handleCardDrawOutcome(res.data);
        }, function() {
            alert('系統異常，請稍後再試。');
            deps.dispatch({ type: 'submitBlocked' });
        });
    }

    // TapPay 當頁刷卡：同一個請求內完成扣款與開獎，成功時直接開結果彈窗，
    // 3D 需驗證時整頁導去驗證頁，結果未定時併入既有的回流輪詢收斂。
    // devScenario 有值時（WTOY_DEV_TOOLS 面板）跳過 getPrime，直接帶模擬情境送出。
    function confirmCardDraw(gatewayId, devScenario) {
        if (requireNotifyEmail() === null) {
            return;
        }
        var invoiceType = $('input[name=wtoy_draw_pay_invoice_type]:checked').val();
        if (!invoiceType) {
            alert('請選擇發票類型');
            return;
        }
        if (invoiceType !== 'company' && !String(paySection.collectInvoiceFields().billing_owp_wcaei_buyer_name || '').trim()) {
            alert('請填寫買受人');
            return;
        }

        if (devScenario) {
            var afterDevSubmit = deps.dispatch({ type: 'submitCard' });
            if (afterDevSubmit.phase !== 'submitting') {
                return;
            }
            submitCardDraw(gatewayId, invoiceType, 'wtoysim:' + devScenario);
            return;
        }

        if (!canSubmitCard(tapPayCardUpdate)) {
            alert('請完整填寫卡片資訊');
            return;
        }

        var afterSubmit = deps.dispatch({ type: 'submitCard' });
        if (afterSubmit.phase !== 'submitting') {
            return;
        }

        tapPayCard.getCardPrime(function(result) {
            if (result.status !== 0) {
                alert('卡片資訊有誤：' + result.msg);
                deps.dispatch({ type: 'submitBlocked' });
                return;
            }

            submitCardDraw(gatewayId, invoiceType, result.card.prime);
        });
    }

    function handleCardDrawOutcome(payload) {
        var action = cardOutcomeAction(payload);

        if (action.type === 'show_result') {
            deps.dispatch({ type: 'resultReady', result: action.result, hasPeel: deps.hasPeel() });
            return;
        }

        if (action.type === 'redirect') {
            sessionStorage.setItem('wtoy_draw_paying', JSON.stringify({
                orderId: payload.order_id,
                productId: deps.getProductId(),
            }));
            deps.dispatch({ type: 'redirecting', url: action.url });
            return;
        }

        if (action.type === 'poll') {
            sessionStorage.setItem('wtoy_draw_paying', JSON.stringify({
                orderId: action.orderId,
                productId: deps.getProductId(),
            }));
            if (drawPollScheduler.start(action.orderId)) {
                deps.dispatch({ type: 'returning', message: statusMessage('paying') });
            }
            return;
        }

        // show_error
        alert(action.message);
        deps.dispatch({ type: 'submitBlocked' });
    }

    // ── 混合支付回流頁輪詢：通知已先到（狀態已 resolving/done）或還在等付款結果都靠這支收斂 ──
    function getDrawReturnOrderId() {
        var params = new URLSearchParams(location.search);
        var fromQuery = params.get('wtoy_draw_order');
        if (fromQuery) {
            // 這顆 query 是 DrawReturnRedirect::urlFor() 依儲值單自己的商品 id 組出來的，
            // 落在這個商品頁本身就代表對得上，不必再比對
            return parseInt(fromQuery, 10);
        }

        var stored = null;
        try {
            stored = JSON.parse(sessionStorage.getItem('wtoy_draw_paying') || 'null');
        } catch (e) {
            stored = null;
        }
        if (!shouldUseStoredDrawOrder(stored, deps.getProductId())) {
            // 不是本商品的付款進行中旗標（使用者中途換頁）：清掉，避免下次還誤判
            sessionStorage.removeItem('wtoy_draw_paying');
            return null;
        }
        return stored.orderId;
    }

    // true 僅在收到後端終局 failed、輪詢已停止時成立：此時清回流狀態不會弄丟任何
    // 還在進行的追蹤，因為已經沒有追蹤在進行了。paying/resolving 期間輪詢仍靠遞迴
    // setTimeout 跑著，orderId 早已 captured，清狀態攔不住它，只會讓重整後接不回這張單、
    // 開獎完成也收不到結果彈窗——所以未終局時關閉彈窗只單純關閉，不動這個旗標以外的狀態。
    var drawReturnTerminal = false;

    function clearDrawReturnState() {
        sessionStorage.removeItem('wtoy_draw_paying');
        if (window.history && window.history.replaceState) {
            var params = new URLSearchParams(location.search);
            if (params.has('wtoy_draw_order')) {
                params.delete('wtoy_draw_order');
                var qs = params.toString();
                window.history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
            }
        }
    }

    function setDrawReturnMessage(text) {
        $('#wtoy_draw_return_status_message').text(text);
        $('#wtoy_draw_return_status_popup').toggleClass('open', !!text);
    }

    /** draw-flow reducer 的 markReturnTerminal effect handler（見呼叫端的 applyDrawFlowEffect）。 */
    function markReturnTerminal() {
        drawReturnTerminal = true;
    }

    /** 彈窗開啟時呼叫，重設「本次是否已套用偏好」旗標（陷阱 3.3）。 */
    function resetPreferenceApplied() {
        preferenceApplied = false;
    }

    // 最近一次成功回應帶回的硬性上限，供傳輸層失敗時的重試判斷使用（見 shouldStopOnTransportFailure）。
    // 上限依該單的收斂機制而異（扣款結果未定的單比較長），但這個值只在「這張單一次都還沒
    // 成功回應過」的窗口內可能沿用到別單的值，第一次成功回應就會被該單自己的上限覆寫；
    // 沿用期間最多影響傳輸層失敗要不要再重試一次，不影響任何終局判定。
    var lastHardCapMs = null;

    // 排程器 runner：跑一次查詢，回報這次要不要停與下一次間隔，所有排程／併發防護交給
    // drawPollScheduler（js/src/lib/draw-poll-scheduler.mjs），這裡只管單次請求的副作用。
    function runDrawStatusPoll(orderId, startedAt) {
        return drawApi.status(orderId).then(function(res) {
            if (!drawPollScheduler.isCurrent(orderId)) {
                // 這次回應送出後、settle 前，使用者已換單（drawPollScheduler.start() 排進
                // 了新單）：這顆回應屬於已放棄的舊鏈，任何副作用（清狀態、渲染結果彈窗、
                // drawReturnTerminal）都不該對使用者已切換去的新單生效，故一律不做、直接
                // 回終局讓排程器接手起跑新單
                return { terminal: true, nextDelayMs: 0 };
            }

            if (!res.success) {
                // AJAX 層失敗（nonce 失效、單不存在），與後端明確判定的 failed 不同語意——
                // 立即清狀態，不留給使用者關閉彈窗才清（那是給後端 failed 判定用的）
                deps.dispatch({ type: 'returnTimeout', message: '本次抽獎未成立，請至訂單查看' });
                return { terminal: true, nextDelayMs: 0 };
            }

            var data = res.data;
            var elapsedMs = Date.now() - startedAt;
            lastHardCapMs = data.polling.hard_cap_ms;

            if (data.state === 'failed') {
                // 只停輪詢、不清回流狀態：sessionStorage 與 URL 上的 order id 留著，
                // 重整頁面會再打一次 draw_status，失敗文案才會持久重現。使用者主動關閉
                // 彈窗（見下方 .wtoy-popup-close handler）才清狀態，不然彈窗永不消失——
                // 此時輪詢已停止，清狀態不會弄丟任何還在進行的追蹤，故標記為終局。
                // 提早在此攔下 failed，故 shouldStopPolling() 對 failed 回 true 這條分支
                // 在這支呼叫端用不到——它是給函式契約本身釘住行為，非死碼。
                // drawReturnTerminal 由 reducer 回的 markReturnTerminal effect 設定
                // （見下方 markReturnTerminal()），不在此直接設，維持單一事實來源。
                deps.dispatch({ type: 'returnFailed', message: data.message || statusMessage('failed') });
                return { terminal: true, nextDelayMs: 0 };
            }

            if (isTimedOut(data.state, elapsedMs, data.polling.hard_cap_ms)) {
                // 逾上限時直接清狀態（不像 failed 留給使用者關閉）：上限由伺服器每次回應
                // 帶回（DrawPollingPlan），已經算進該單所屬的收斂機制要多久才會第一次看到
                // 它，走到這裡代表沒有明確終局訊號可等，清掉讓下次載入重新判斷比留著一個
                // 模稜兩可的狀態更安全。上限恆取自 data.polling，不在前端寫死任何秒數
                deps.dispatch({ type: 'returnTimeout', message: data.message || '本次抽獎未成立，請至訂單查看' });
                return { terminal: true, nextDelayMs: 0 };
            }

            if (shouldStopPolling(data.state, elapsedMs, data.polling.hard_cap_ms)) {
                // data.state === 'done'：交結果彈窗接手，回流狀態訊息不再需要
                deps.dispatch({ type: 'returnResolved', result: data.result, hasPeel: deps.hasPeel() });
                return { terminal: true, nextDelayMs: 0 };
            }

            deps.dispatch({ type: 'returnTick', message: statusMessage(data.state, data.message) });
            return { terminal: false, nextDelayMs: nextIntervalMs(data.polling) };
        }, function() {
            if (!drawPollScheduler.isCurrent(orderId)) {
                return { terminal: true, nextDelayMs: 0 };
            }

            var elapsedMs = Date.now() - startedAt;
            if (shouldStopOnTransportFailure(elapsedMs, lastHardCapMs)) {
                deps.dispatch({ type: 'returnTimeout', message: '本次抽獎未成立，請至訂單查看' });
                return { terminal: true, nextDelayMs: 0 };
            }
            // 傳輸失敗不視為逾時，沿用信用卡節奏重試，交由硬性上限收斂
            return { terminal: false, nextDelayMs: DEFAULT_INTERVAL_MS };
        });
    }

    var drawPollScheduler = createDrawPollScheduler({
        poll: runDrawStatusPoll,
        schedule: function(fn, delayMs) { return setTimeout(fn, delayMs); },
        cancel: function(handle) { clearTimeout(handle); },
        now: function() { return Date.now(); },
    });

    document.addEventListener(DRAW_STATUS_EVENT, function(e) {
        var orderId = drawStatusOrderId(e.detail);
        if (orderId === null || !drawPollScheduler.isCurrent(orderId)) {
            return;
        }
        drawPollScheduler.wake();
    });

    // 供「已付款，接手回流輪詢」的其他入口共用（如待付單彈窗查到已付款時）——
    // 與 initReturnPolling() 差別只在 orderId 從哪裡來，事件與訊息完全一致。
    function startReturnPollingForOrder(orderId) {
        if (drawPollScheduler.start(orderId)) {
            drawReturnTerminal = false;
            deps.dispatch({ type: 'returnStart', message: statusMessage('paying') });
        }
    }

    function initReturnPolling() {
        var orderId = getDrawReturnOrderId();
        if (!orderId) {
            return;
        }
        startReturnPollingForOrder(orderId);
    }

    // 使用者主動關閉才清回流狀態：進行中（paying/resolving）的訊息下一次輪詢
    // 還會把彈窗重新打開（設計如此，輪詢仍在跑），只有 failed 這個終局態
    // 已停止輪詢，關閉後才會真的消失不再重現。
    // 綁在 document 上做事件委派：本行在工廠本體頂層執行，一番賞頁的主腳本
    // 印在 <head>，此刻彈窗 markup 還沒進 DOM，直接綁會匹配到 0 個元素而靜默
    // 失效；document.body 在這個時點也還是 null，同樣綁不上，故委派根只能是
    // document。
    $(document)
        .off('click.wtoyDrawReturnClose')
        .on('click.wtoyDrawReturnClose', '#wtoy_draw_return_status_popup', function(e) {
            if (e.target === this || $(e.target).hasClass('wtoy-popup-close')) {
                // 未終局（paying/resolving）時輪詢仍在跑，關閉只是關閉，不清狀態——
                // 清了也攔不住下一次輪詢把彈窗打開，卻會讓重整後接不回這張單、
                // 開獎完成也收不到結果彈窗。只有 failed 已停止輪詢才清得乾淨。
                $('#wtoy_draw_return_status_popup').removeClass('open');
                if (drawReturnTerminal) {
                    setDrawReturnMessage('');
                    clearDrawReturnState();
                }
                // 非終局（paying/resolving）時 phase 不是 settled，reducer 端的
                // guard 會直接 no-op，dispatch 本身對狀態機是安全的
                deps.dispatch({ type: 'returnAcknowledged' });
            }
        });

    return {
        renderGateways: paySection.renderGateways,
        setupTapPayCardFields: tapPayCard.setupTapPayCardFields,
        renderInvoiceFields: paySection.renderInvoiceFields,
        renderInvoiceTypes: paySection.renderInvoiceTypes,
        capturePaymentChoice: paySection.capturePaymentChoice,
        restorePaymentChoice: paySection.restorePaymentChoice,
        renderDrawPayPopup: renderDrawPayPopup,
        requireNotifyEmail: requireNotifyEmail,
        confirmReserve: confirmReserve,
        submitCardDraw: submitCardDraw,
        confirmCardDraw: confirmCardDraw,
        handleCardDrawOutcome: handleCardDrawOutcome,
        initReturnPolling: initReturnPolling,
        startReturnPollingForOrder: startReturnPollingForOrder,
        setDrawReturnMessage: setDrawReturnMessage,
        clearDrawReturnState: clearDrawReturnState,
        markReturnTerminal: markReturnTerminal,
        resetPreferenceApplied: resetPreferenceApplied,
        collapseTreasureStopHelp: collapseTreasureStopHelp,
    };
}
